import { MagnifyingGlassIcon } from '@phosphor-icons/react';
import { useField } from 'formik';
import { useMemo, useState } from 'react';

import { Button } from '../../components/button/Button';
import { Combobox } from '../../components/combobox/Combobox';
import { InputWrapper } from '../../components/InputWrapper';
import { useSuppliers, type SupplierListEntry } from './models';
import { getFullUrl } from '../../utils/url';
import { openTab } from '../../utils/windows';
import { CreateSupplierDialog } from './components/CreateSupplierDialog';

export type Supplier = SupplierListEntry;

const displaySupplier = (supplier: Supplier) => {
  if (supplier.deactivationReason) {
    return `${supplier.name} (gedeactiveerd)`;
  }

  return supplier.name;
};

export interface ISupplierComboboxFieldProps {
  name: string;
  labelText?: string;
  helperText?: string;
  isDisabled?: boolean;
  isNullable?: boolean;
  showDeactivated?: boolean;
}

export function SupplierComboboxField(props: ISupplierComboboxFieldProps) {
  const { name, helperText, isDisabled, isNullable, labelText, showDeactivated } = props;
  const [searchValue, setSearchValue] = useState('');
  const [createState, setCreateState] = useState({ isOpen: false, name: '' });
  const { data } = useSuppliers({
    search: searchValue,
  });

  const suppliers = useMemo(() => {
    return (data ?? []).filter((v) => showDeactivated || !v.deactivationReason);
  }, [data, showDeactivated]);

  const [field, meta, helpers] = useField({ name });
  const selectedItem = field.value ?? null;

  const openSupplier = (id: number) => openTab(getFullUrl(`/internal/suppliers/${id}/general`));
  return (
    <div className="flex gap-2">
      <InputWrapper
        labelText={labelText ?? 'Vervoerder'}
        invalidText={meta.touched ? meta.error : undefined}
        helperText={helperText}
      >
        <Combobox
          keyName="id"
          placeholder="Zoek een vervoerder..."
          onQueryChange={setSearchValue}
          display={displaySupplier}
          items={suppliers}
          selectedItem={selectedItem}
          onSelect={(val) => {
            helpers.setValue(val);
          }}
          createLabel="Maak vervoerder aan"
          onCreate={(query) => setCreateState({ isOpen: true, name: query })}
          onBlur={() => helpers.setTouched(true)}
          isDisabled={isDisabled}
          isInvalid={Boolean(meta.touched && meta.error)}
          isNullable={isNullable}
        />
      </InputWrapper>
      <div className="flex items-center">
        <Button
          color="secondary"
          onTrigger={() => {
            if (selectedItem) {
              openSupplier(selectedItem.id);
            }
          }}
          isDisabled={!selectedItem}
        >
          <MagnifyingGlassIcon className="w-4 h-4" />
        </Button>
      </div>

      <CreateSupplierDialog
        isOpen={createState.isOpen}
        initialName={createState.name}
        onClose={() => setCreateState({ isOpen: false, name: '' })}
        onCreated={(created) => {
          helpers.setValue(created);
          setCreateState({ isOpen: false, name: '' });
        }}
      />
    </div>
  );
}
